import { useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	Button,
	Box,
	Typography,
	Chip,
	Alert,
	Card,
	CardContent,
	CardActionArea
} from '@mui/material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import FuseLoading from '@fuse/core/FuseLoading';
import { useGetAddressesListQuery } from '@/store/services/address';
import { useNavigate } from 'react-router-dom';

type Address = {
	id: number;
	name: string | null;
	location_name: string | null;
	type: string | null;
	is_enabled: number;
	application: {
		id: number;
		application_number: string;
		address_id: number;
	} | null;
	documents?: Array<{
		id: number;
		address_id: number;
		file_name: string;
		file_type: string;
		document_file_url: string;
	}>;
};

type SelectAddressDialogProps = {
	open: boolean;
	onClose: () => void;
	onSelect: (address: Address) => void;
};

function SelectAddressDialog({ open, onClose, onSelect }: SelectAddressDialogProps) {
	const navigate = useNavigate();
	const { data: addressesResponse, isLoading: isLoadingAddresses } = useGetAddressesListQuery({});
	const addresses = addressesResponse?.data || [];
	const hasDisabledAddresses = addresses.some(
		(address: Address) => address.application !== null || address.is_enabled === 0
	);

	// Auto-select address if only one exists and it's not disabled
	useEffect(() => {
		if (!open || addresses.length === 0) return;

		const selectableAddresses = addresses.filter(
			(address: Address) => address.application === null && address.is_enabled !== 0
		);

		// Auto-select when exactly one selectable address exists
		if (selectableAddresses.length === 1) {
			onSelect(selectableAddresses[0]);
		}
	}, [open, addresses, onSelect]);

	const handleAddressSelect = (address: Address) => {
		onSelect(address);
	};

	const handleClose = () => {
		onClose();
	};

	const getTypeLabel = (type: string | null) => {
		if (!type) return 'N/A';

		return type.charAt(0).toUpperCase() + type.slice(1);
	};

	const getTypeColor = (type: string | null) => {
		if (!type) return 'default';

		const typeMap: Record<string, 'primary' | 'success' | 'warning'> = {
			apartment: 'primary',
			house: 'success',
			office: 'warning',
			residential: 'primary'
		};

		return typeMap[type] || 'default';
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle>Select Address</DialogTitle>
			<DialogContent>
				<Box className="flex flex-col gap-4 mt-2">
					<Box>
						<Typography
							variant="body1"
							className="mb-4 font-medium"
						>
							Select an address for this asset:
						</Typography>

						{!isLoadingAddresses && addresses.length > 0 && hasDisabledAddresses && (
							<Alert severity="info" className="mb-4">
								You can not use the disabled address due to being used in an application/asset.
							</Alert>
						)}

						{isLoadingAddresses ? (
							<Box className="flex justify-center items-center py-12">
								<FuseLoading />
							</Box>
						) : addresses.length === 0 ? (
							<Box className="flex flex-col items-center justify-center py-12 gap-4">
								<Typography
									variant="body1"
									className="text-gray-600 text-center"
								>
									There is no address first add address
								</Typography>
								<Button
									variant="contained"
									color="secondary"
									onClick={() => {
										onClose();
										navigate('/apps/addresses');
									}}
									startIcon={<FuseSvgIcon>heroicons-outline:plus</FuseSvgIcon>}
								>
									Add Address
								</Button>
							</Box>
						) : (
							<Box className="grid grid-cols-2 gap-3">
								{addresses.map((address: Address) => {
									const isDisabled =
										address.application !== null || address.is_enabled === 0;
									return (
										<Card
											key={address.id}
											sx={{
												border: '1px solid',
												borderColor: 'divider',
												opacity: isDisabled ? 0.6 : 1,
												cursor: isDisabled ? 'not-allowed' : 'default',
												'&:hover': isDisabled
													? {}
													: {
															borderColor: 'primary.main',
															boxShadow: 2
														}
											}}
										>
											<CardActionArea
												onClick={() => !isDisabled && handleAddressSelect(address)}
												disabled={isDisabled}
											>
												<CardContent>
													<Box className="flex items-center justify-between">
														<Box className="flex-1">
															<Typography
																variant="h6"
																className="font-semibold mb-1 text-lg"
															>
																{address.name || 'N/A'}
															</Typography>
															<Typography
																variant="body2"
																className="text-gray-600 mb-2"
															>
																{address.location_name || 'N/A'}
															</Typography>
															<Box className="flex items-center gap-2">
																<Chip
																	label={getTypeLabel(address.type)}
																	color={getTypeColor(address.type)}
																	size="small"
																/>
																{address.application !== null && (
																	<Chip
																		label="In Use"
																		color="default"
																		size="small"
																		variant="outlined"
																	/>
																)}
																{address.is_enabled === 0 && (
																	<Chip
																		label="Disabled"
																		color="default"
																		size="small"
																		variant="outlined"
																	/>
																)}
															</Box>
														</Box>
														{!isDisabled && (
															<FuseSvgIcon className="text-gray-400">
																heroicons-outline:chevron-right
															</FuseSvgIcon>
														)}
													</Box>
												</CardContent>
											</CardActionArea>
										</Card>
									);
								})}
							</Box>
						)}
					</Box>
				</Box>
			</DialogContent>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					gap: 2,
					p: 2
				}}
			>
				<Button onClick={handleClose}>Cancel</Button>
			</Box>
		</Dialog>
	);
}

export default SelectAddressDialog;
